import * as Sentry from '@sentry/react';

import { type AppError } from '.';
import { getConfig } from '../config';
import { getQueryClient } from '../sdk/request';
import { getStores } from '../services/storage';
import { getAuthUserFromCache } from '../services/storage/users-queries';
import { getSelectedBrand } from '../shared/brand';
import { getSelectedOrganization } from '../shared/organization';

// let lastUserIp: string | undefined = undefined;

export function errorTrackingInit(dsn: string, tags?: { [key: string]: any }) {
  const config = getConfig();

  Sentry.init({
    dsn,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    environment: config.env,

    _experiments: { enableLogs: true },

    initialScope: {
      tags: {
        ...(tags ?? {}),
      },
    },
    integrations: [
      Sentry.captureConsoleIntegration({
        levels: ['error', 'warn'],
      }),
      // Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: false,
        networkDetailAllowUrls: [/blym.co\/|\/writer|\/localhost/],
        networkRequestHeaders: ['X-Custom-Header'],
        networkResponseHeaders: ['X-Custom-Header'],
      }),
    ],
  });
}

export function getErrorTracking() {
  return Sentry;
}

function shouldReportError(err: AppError): boolean {
  const networkErrorMessages = [
    'Network Error',
    'timeout',
    'socket hang up',
    'Network request failed',
    'websocket error',
  ];

  // Ignore errors if the message indicates a network error that is common and usually transient
  const errMsg = err.message.toLowerCase().replace(/ /g, '');
  if (
    networkErrorMessages.some(function (errorMessage) {
      return errMsg.includes(errorMessage.toLowerCase().replace(/ /g, ''));
    })
  ) {
    return false;
  }

  return true;
}

export function addTrackCrumb(breadcrumb: Sentry.Breadcrumb) {
  return Sentry.addBreadcrumb(breadcrumb);
}

function setTrackingUser(
  userId?: string | null,
  token?: string | null,
  currOrgId?: string | null,
  currBrandId?: string | null
) {
  if (userId == null) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: userId,
    username: currOrgId ?? undefined,

    // ip_address: lastUserIp ?? undefined,
  });
  Sentry.setTag('userId', userId);
  Sentry.setTag('token', token);
  Sentry.setTag('organizationId', currOrgId);
  Sentry.setTag('brandId', currBrandId);

  // TODO: finding the ip is failing
  // try to find the ip, no problem if it doesn't
  // if (lastUserIp != null) {
  //   return;
  // }

  // fetch('https://api.ipify.org?format=json')
  //   .then((response) => response.json())
  //   .then(function (data) {
  //     lastUserIp = (data?.ip as string | undefined | null) ?? undefined;
  //
  //     // no point in setting it when it didnt arrive
  //     if (lastUserIp == null) {
  //       return;
  //     }
  //
  //     return setTrackingUser(userId, token, currOrgId, currBrandId);
  //   })
  //   .catch(function (_err) {
  //     // no problem, just ignore
  //   });
}

export function trackError(
  err: AppError,
  info?: {
    [key: string]: any;
  }
) {
  if (!shouldReportError(err)) {
    // we can ignore if we don't want to report
    return;
  }

  const parsedInfo = info ?? {};
  const infoKeys = Object.keys(parsedInfo);

  // try and figure if the capture should be a context, tag...
  for (let i = 0; i < infoKeys.length; i += 1) {
    const key = infoKeys[i];
    if (key.indexOf('Id') === -1) {
      continue;
    }

    // ids are special cases we could easily set the tags for
    const value = parsedInfo[key] as string;
    Sentry.setTag(key, value);
  }

  // contexts that we know of
  Sentry.setContext('global', {
    currentPath: window.location.pathname,
    ...parsedInfo,
  });

  // setup the user data and finally capture the problem
  Promise.resolve()
    .then(async function () {
      // Use TanStack Query cache for user data (synchronous, no network call)
      const queryClient = getQueryClient();
      const authUser = getAuthUserFromCache(queryClient);
      const currentOrg = getSelectedOrganization(authUser);
      const currentBrand = getSelectedBrand(authUser);

      // Auth token is provided by Clerk and stored in Zustand
      const token = await getStores().users.getState().getAuthToken();

      // update with whatever we have
      setTrackingUser(authUser?._id, token, currentOrg?._id, currentBrand?._id);
    })
    .catch(function (_err) {
      // do nothing for tracking exceptions
    })
    .finally(function () {
      return Sentry.captureException(err, parsedInfo);
    });
}
